/**
 * Email verification links often open in a different browser / in-app
 * webview with no session (Sam, Oct 11 2026: link → login → still "verify"
 * loop). Sharetribe needs a signed-in user to apply the token, so we keep the
 * token on this device and apply it right after the next sign-in.
 */
const KEY = 'sl-pending-verify-token';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const store = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

export const savePendingVerifyToken = token => {
  const s = store();
  if (!s || !token) {
    return;
  }
  try {
    s.setItem(KEY, JSON.stringify({ token: `${token}`, at: Date.now() }));
  } catch (e) {
    // ignore
  }
};

export const readPendingVerifyToken = () => {
  const s = store();
  if (!s) {
    return null;
  }
  try {
    const v = JSON.parse(s.getItem(KEY) || 'null');
    if (!v || !v.token || Date.now() - v.at > MAX_AGE_MS) {
      return null;
    }
    return v.token;
  } catch (e) {
    return null;
  }
};

export const clearPendingVerifyToken = () => {
  const s = store();
  if (s) {
    try {
      s.removeItem(KEY);
    } catch (e) {
      // ignore
    }
  }
};

let applying = false;

/**
 * After currentUser loads: apply a saved token for an unverified user.
 *
 * @param {Object} sdk
 * @param {Object} currentUser
 * @param {Function} onVerified called after success (refresh the user)
 */
export const applyPendingVerifyToken = (sdk, currentUser, onVerified) => {
  const verified = currentUser?.attributes?.emailVerified;
  const token = readPendingVerifyToken();
  if (!token) {
    return;
  }
  if (verified === true && !currentUser?.attributes?.pendingEmail) {
    clearPendingVerifyToken();
    return;
  }
  if (applying || !sdk?.currentUser?.verifyEmail) {
    return;
  }
  applying = true;
  sdk.currentUser
    .verifyEmail({ verificationToken: token })
    .then(() => {
      clearPendingVerifyToken();
      if (typeof onVerified === 'function') {
        onVerified();
      }
    })
    .catch(e => {
      // Bad / used / other-account token: drop it so we never loop.
      if (e?.status && e.status !== 429 && e.status < 500) {
        clearPendingVerifyToken();
      }
    })
    .finally(() => {
      applying = false;
    });
};
